import {
  MousePointer2,
  Plus,
  Crop,
  Pencil,
  Type,
  Square,
  Eraser,
  PaintBucket,
  Pipette,
  WandSparkles,
} from 'lucide-react';
import type { EditorTool } from '../types';

interface ToolPaletteProps {
  activeTool: EditorTool;
  onToolSelect: (tool: EditorTool) => void;
  onRemoveBackground: () => void;
  isRemovingBackground: boolean;
  canRemoveBackground: boolean;
}

const tools: {
  id: EditorTool;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}[] = [
  { id: 'move', icon: MousePointer2, label: 'Move (V)' },
  { id: 'marquee', icon: Plus, label: 'Select (M)' },
  { id: 'crop', icon: Crop, label: 'Crop (C)' },
  { id: 'draw', icon: Pencil, label: 'Draw (B)' },
  { id: 'text', icon: Type, label: 'Text (T)' },
  { id: 'shape', icon: Square, label: 'Shape (U)' },
  { id: 'eraser', icon: Eraser, label: 'Eraser (E)' },
  { id: 'fill', icon: PaintBucket, label: 'Fill (G)' },
  { id: 'eyedropper', icon: Pipette, label: 'Eyedropper (I)' },
];

export function ToolPalette({
  activeTool,
  onToolSelect,
  onRemoveBackground,
  isRemovingBackground,
  canRemoveBackground,
}: ToolPaletteProps) {
  return (
    <div className="flex flex-col items-center gap-1 w-14 bg-background border-r border-border py-2 px-1">
      {tools.map((tool) => {
        const Icon = tool.icon;
        const isActive = activeTool === tool.id;
        return (
          <button
            key={tool.id}
            type="button"
            title={tool.label}
            className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
            onClick={() => onToolSelect(tool.id)}
          >
            <Icon className="h-5 w-5" />
          </button>
        );
      })}

      {/* Separator between tool selectors and one-shot actions. */}
      <div className="w-8 h-px bg-border my-1" />

      {/* One-shot actions (don't switch the active tool). */}
      <button
        type="button"
        title={isRemovingBackground ? 'Removing background…' : 'Remove background'}
        className="w-10 h-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:pointer-events-none"
        onClick={onRemoveBackground}
        disabled={!canRemoveBackground || isRemovingBackground}
      >
        <WandSparkles className="h-5 w-5" />
      </button>
    </div>
  );
}
